import { useMemo } from "react";
import type { LadderPrize } from "@/lib/types";
import { buildPathD, columnX } from "../utils";

interface LadderBoardProps {
  slotCnt: number;
  rungs: [number, number][] | null;
  reveal: 0 | 1;
  prizes: LadderPrize[];
  ends: number[] | null;
  taken: number[];
  mySlot: number | null;
  clickable: boolean;
  onSlotClick?: (slot: number) => void;
  animateSlot: number | null;
  onAnimateEnd?: () => void;
}

const MARGIN = 36;
const WIDTH = 1000;
const TOP_PAD = 56;
const ROW_H = 44;
const BOTTOM_PAD = 48;

// 사다리 보드 (SVG). 세로줄 + 가로선 + 출발칸 + 내 경로 애니메이션.
export default function LadderBoard({
  slotCnt,
  rungs,
  reveal,
  prizes,
  ends,
  taken,
  mySlot,
  clickable,
  onSlotClick,
  animateSlot,
  onAnimateEnd,
}: LadderBoardProps) {
  const xs = useMemo(() => columnX(slotCnt, WIDTH, MARGIN), [slotCnt]);

  const maxLevel = useMemo(() => {
    if (!rungs || rungs.length === 0) return 0;
    return Math.max(...rungs.map((r) => r[0]));
  }, [rungs]);

  const levels = maxLevel + 1;
  const height = TOP_PAD + levels * ROW_H + BOTTOM_PAD;
  const lineBottom = TOP_PAD + levels * ROW_H;

  const prizeByNo = useMemo(() => {
    const map = new Map<number, LadderPrize>();
    prizes.forEach((p) => map.set(p.no, p));
    return map;
  }, [prizes]);

  // 애니메이션 중이면 animateSlot, 아니면 내 칸 경로를 그린다.
  const pathSlot = animateSlot ?? (reveal === 1 ? mySlot : null);
  const animating = animateSlot != null;

  const pathD = useMemo(() => {
    if (pathSlot == null || !rungs) return "";
    return buildPathD(pathSlot, rungs, xs, TOP_PAD, ROW_H);
  }, [pathSlot, rungs, xs]);

  return (
    <div className="relative w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${WIDTH} ${height}`}
        className="w-full min-w-[560px]"
        role="img"
        aria-label="사다리게임 보드"
      >
        {/* 세로줄 */}
        {xs.map((x, i) => (
          <line
            key={`v-${i}`}
            x1={x}
            y1={TOP_PAD}
            x2={x}
            y2={lineBottom}
            className="text-background-300"
            stroke="currentColor"
            strokeWidth={2}
          />
        ))}

        {/* 가로선 (rungs) — reveal=1 일 때만 */}
        {reveal === 1 &&
          rungs &&
          rungs.map((r, i) => {
            const y = TOP_PAD + r[0] * ROW_H + ROW_H / 2;
            return (
              <line
                key={`r-${i}`}
                x1={xs[r[1]]}
                y1={y}
                x2={xs[r[1] + 1]}
                y2={y}
                className="text-foreground-500"
                stroke="currentColor"
                strokeWidth={2}
              />
            );
          })}

        {/* 도착칸 경품 색 점 — reveal=1 일 때만 */}
        {reveal === 1 &&
          ends &&
          xs.map((x, i) => {
            const prize = prizeByNo.get(ends[i] ?? -1);
            const color = prize?.color || "#cbd5e1";
            return (
              <circle
                key={`end-${i}`}
                cx={x}
                cy={height - BOTTOM_PAD + 16}
                r={7}
                fill={color}
              />
            );
          })}

        {/* 내 경로 */}
        {pathD && (
          <path
            d={pathD}
            className={`ladder-path text-accent-600 ${animating ? "is-animating" : ""}`}
            stroke="currentColor"
            strokeWidth={5}
            onAnimationEnd={onAnimateEnd}
          />
        )}

        {/* 출발칸 (숫자 버튼) */}
        {xs.map((x, i) => {
          const isTaken = taken.includes(i);
          const isMine = mySlot === i;
          const canClick = clickable && !isTaken && !isMine && mySlot == null;
          return (
            <g
              key={`slot-${i}`}
              onClick={canClick ? () => onSlotClick?.(i) : undefined}
              style={{ cursor: canClick ? "pointer" : "default" }}
            >
              <circle
                cx={x}
                cy={TOP_PAD / 2}
                r={16}
                fill={
                  isMine
                    ? "oklch(var(--accent-500))"
                    : isTaken
                      ? "oklch(var(--background-200))"
                      : "oklch(var(--background-50))"
                }
                stroke={
                  isMine
                    ? "oklch(var(--accent-600))"
                    : "oklch(var(--background-300))"
                }
                strokeWidth={2}
              />
              <text
                x={x}
                y={TOP_PAD / 2}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={13}
                fontWeight={700}
                fill={
                  isMine
                    ? "oklch(var(--primary-950))"
                    : isTaken
                      ? "oklch(var(--foreground-400))"
                      : "oklch(var(--foreground-700))"
                }
              >
                {i + 1}
              </text>
            </g>
          );
        })}
      </svg>

      {/* 결과 비공개 오버레이 */}
      {reveal === 0 && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="flex flex-col items-center gap-1 rounded-xl bg-background-50/85 px-6 py-4">
            <span className="font-heading text-3xl font-bold text-foreground-400">?</span>
            <span className="text-xs text-foreground-500">결과는 종료 후 공개됩니다</span>
          </div>
        </div>
      )}
    </div>
  );
}